import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@economic/gaudi';
import { refreshCurrentLegacyPage } from '@economic/react-app';

import { RouteDisclosure } from '../../../../../shared/components/RouteDisclosure';
import { useLocalization, t } from '../../../../../shared/components/l10n/l10n';
import useMutation from '../../../../../shared/hooks/useMutation';
import { SendByType } from '../../invoices/book-and-send/types';
import { splitEmails } from '../../invoices/utils';
import { usePostQuery } from '../../shared/hooks/usePostQuery';
import {
    SendInvoiceEmailFromArchiveInformationResponse,
    SendEmailFromArchiveRequest as SendInvoiceEmailFromArchiveRequest,
    SendInvoiceElectronicallyFromArchiveRequest,
} from '../types';
import { SendInvoiceDialog } from './SendInvoiceDialog';
import { useEmailTemplates } from '../../invoices/utils/useEmailTemplates';
import { InvoiceLayoutType } from '../../../../../../types/internal-api-extra';
import { emailTemplateFormState } from '../../invoices/utils/emailTemplateFormState';
import { emailOptionsFromTemplates } from '../../invoices/utils/emailOptionsFromTemplates';

const sendFromArchive = useMutation<SendInvoiceEmailFromArchiveRequest, SendInvoiceEmailFromArchiveInformationResponse>(
    `sales/invoice/SendEmailFromArchive`
);
const sendElectronically = useMutation<SendInvoiceElectronicallyFromArchiveRequest>(
    `sales/invoice/SendInvoiceElectronicallyFromArchive`
);

function SendInvoiceDialogContainer() {
    const toast = useToast();
    const navigate = useNavigate();
    const location = useLocation();
    const invoiceId = (location.state as { invoiceId: number }).invoiceId;

    const textsLoading = useLocalization(textIds);

    const [sendingError, setSendingError] = useState<boolean>(false);
    const [sending, setSending] = useState<boolean>(false);

    const { loading: informationLoading, data: informationData, error: informationLoadingError } = usePostQuery<
        SendInvoiceEmailFromArchiveInformationResponse
    >('sales/invoice/SendEmailFromArchiveInformation', { invoiceId }, !invoiceId);

    const emailTemplateType = !!informationData
        ? informationData.isCreditNote
            ? InvoiceLayoutType.CreditNote
            : InvoiceLayoutType.Invoice
        : undefined;

    const { data: templates, loading: templatesLoading } = useEmailTemplates(emailTemplateType);

    const tranformedInformation = useMemo(() => {
        return (
            informationData && {
                ...informationData,
                sendByType:
                    informationData.canBeSentElectronically && !informationData.defaultDisableEInvoicing
                        ? SendByType.oioxml
                        : SendByType.email,
                emailOptions: {
                    ...informationData.emailOptions,
                    recipients: (informationData.emailOptions.recipients || '').join(' '),
                    recipientsCc: (informationData.emailOptions.recipientsCc || '').join(' '),
                    recipientsBcc: (informationData.emailOptions.recipientsBcc || '').join(' '),
                    saveSubject: false,
                    saveBody: false,
                    ...emailOptionsFromTemplates(templates),
                },
                ...emailTemplateFormState(templates),
            }
        );
    }, [informationData, templates]);

    async function onFormSubmit(values) {
        setSending(true);
        var success = true;
        try {
            if (values.sendByType === SendByType.email) {
                await sendFromArchive({
                    invoiceId,
                    ...values.emailOptions,
                    recipients: splitEmails(values.emailOptions.recipients),
                    recipientsCc: splitEmails(values.emailOptions.recipientsCc),
                    recipientsBcc: splitEmails(values.emailOptions.recipientsBcc),
                } as SendInvoiceEmailFromArchiveRequest).catch(() => {
                    success = false;
                });
            } else if (values.sendByType === SendByType.oioxml) {
                await sendElectronically({ invoiceId }).catch(() => {
                    success = false;
                });
            }
            if (success) {
                setSending(false);
                toast.success(t`TOAST_INVOICE_SEND_SUCCESS`);

                refreshContentFrameAfterSuccess();
                hide();
            } else {
                setSending(false);
                setSendingError(true);
            }
        } catch (error) {
            if (error && error.response && error.response.status >= 500) {
                setSendingError(true);
            }
        }
    }

    function refreshContentFrameAfterSuccess() {
        refreshCurrentLegacyPage('content');
    }

    function hide() {
        navigate(location.pathname);
    }

    const disclosure = <RouteDisclosure routeTrigger="#send-invoice" />;

    if (!invoiceId) {
        return null;
    }

    return (
        <SendInvoiceDialog
            loading={textsLoading || informationLoading || templatesLoading || sending}
            initialData={tranformedInformation}
            error={informationLoadingError || sendingError}
            disclosure={disclosure}
            onSend={onFormSubmit}
            onHide={hide}
        />
    );
}

const textIds = [
    'FORM_ARCHIVESEND_INVOICE_HEADER',
    'FORM_ARCHIVESEND_DOCUMENT_TYPE_CREDIT_NOTE',
    'FORM_ARCHIVESEND_DOCUMENT_TYPE_INVOICE',
    'FORM_ARCHIVESEND_SEND_BY_EMAIL',
    'FORM_ARCHIVESEND_SEND_BY_EINVOICE',
    'FORM_ARCHIVESEND_BUTTON_CANCEL',
    'FORM_ARCHIVESEND_BUTTON_SEND',
    'FORM_ARCHIVESEND_ERRORS_CANNOTSEND',
    'FORM_BOOKANDSEND_ERRORS_UNKNOWN_ERROR',
    'SHARED_FORM_OK',
    'TOAST_INVOICE_SEND_SUCCESS',

    'LOADING_TEXT1',
    'LOADING_TEXT2',
    'LOADING_TEXT3',

    'FORM_BOOKANDSEND_SELECT_ATTACHMENT_ERROR',
    'FORM_BOOKANDSEND_SELECT_EMAIL_RECIPIENT_ERROR',
];

export default SendInvoiceDialogContainer;
